/**
 * ReviewQueuePage — `/admin/needs-review` (BON-381).
 *
 * One page carrying both queues: bookings and checkout sessions share the four
 * review columns, so they queue together, but they sit in two separately headed
 * sections because a booking is a record that exists and a checkout session is
 * one that never became a booking — the operator's next move differs.
 *
 * Rows are rendered in the order the API sent them (oldest first): reasons are
 * free text, so there is nothing to rank, and age is the signal. Resolving is
 * NOT a row action — it requires a note, so it lives on the detail page.
 *
 * Layout switches on viewport: phone gets cards plus an Open/Resolved tab pair,
 * tablet and desktop share the table view.
 */
import { useViewport } from '../../hooks/useViewport';
import { useAppMode } from '../../shared/nav/use-app-mode';
import { DashboardsPage } from '../dashboards/DashboardsPage';

import ReviewQueueBody from './components/ReviewQueueBody';
import { ReviewPageHeader } from './ReviewPageHeader';
import { ReviewCategoryFilter, ReviewQueueTabs, ShowResolvedToggle } from './ReviewQueueControls';
import { resolvedOnPage } from './reviewQueueModel';
import { useReviewQueueState } from './useReviewQueueState';

/** The tabs' `aria-controls` target. */
const QUEUE_PANEL_ID = 'review-queue-panel';

import '../dashboards/dashboards.css';

export default function ReviewQueuePage() {
  const { isPhone } = useViewport();
  const s = useReviewQueueState(isPhone);
  // The workshop links in the rows resolve into the viewer's own shell.
  const shell = useAppMode().mode;

  return (
    <DashboardsPage
      header={
        <ReviewPageHeader
          crumb="Customers › Needs review"
          title="Needs review"
          compact={isPhone}
          actions={
            isPhone ? null : (
              <ShowResolvedToggle
                checked={s.showResolved}
                // Count the rows when we have them; quote the API's region-wide
                // total only while they are not loaded (U6).
                resolvedCount={resolvedOnPage(s.data, s.data.resolvedCount, s.showResolved)}
                onChange={s.setShowResolved}
              />
            )
          }
        />
      }
    >
      {/* Above the sections on both layouts: it changes what the sections
            CONTAIN, so it has to read as belonging to them rather than to the
            page header. */}
      <ReviewCategoryFilter active={s.category} onChange={s.setCategory} />
      {isPhone && (
        <ReviewQueueTabs
          active={s.tab}
          openCount={s.data.openCount}
          resolvedCount={s.data.resolvedCount}
          onChange={s.setTab}
          panelId={QUEUE_PANEL_ID}
        />
      )}
      {/* The tabs' `aria-controls` target. On desktop there are no tabs, so it
            is a plain region rather than a tabpanel — a tabpanel with no tablist
            is a lie about the structure. */}
      <div
        id={QUEUE_PANEL_ID}
        role={isPhone ? 'tabpanel' : 'region'}
        aria-label={isPhone ? undefined : 'Flagged items'}
        aria-labelledby={isPhone ? `${QUEUE_PANEL_ID}-tab-${s.tab}` : undefined}
        // `aria-busy` rather than a spinner: the rows stay, and assistive tech
        // is told they are being updated.
        aria-busy={s.refreshing}
        style={{ transition: 'opacity 120ms', opacity: s.refreshing ? 0.7 : 1 }}
      >
        <ReviewQueueBody s={s} isPhone={isPhone} shell={shell} />
      </div>
    </DashboardsPage>
  );
}
